import React, { ChangeEvent, useState, useRef, InputHTMLAttributes, useEffect } from 'react';
import SelectVariableModal, { ListItem } from 'src/components/modals/SelectVariableModal/SelectVariableModal';
import classNames from 'classnames';
import { Control, Controller } from 'react-hook-form';
import { t } from 'i18next';
import Icon, { IconName } from '../Icon/Icon';
import styles from './Input.module.scss';

export interface InputProps {
  label?: string;
  /** DOM id of the rendered `<input>`, so an external `<label htmlFor>` can address it. */
  id?: string;
  value?: InputHTMLAttributes<HTMLInputElement>['value'] | null;
  type?: string;
  name?: string;
  disabled?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  error?: boolean;
  icon?: IconName;
  clearable?: boolean;
  hasVariableInsertion?: boolean;
  variables?: ListItem[];
  errorMessage?: string;
  secret?: boolean;
  min?: number | string;
  max?: number | string;
  step?: number | string;
  defaultValue?: InputHTMLAttributes<HTMLInputElement>['value'] | null;
  isInfoDisplay?: boolean;
  maxLength?: number;
  autoComplete?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      id,
      value,
      type = 'text',
      name,
      disabled,
      onChange,
      onKeyDown,
      placeholder,
      className,
      error,
      icon,
      clearable,
      hasVariableInsertion,
      variables,
      errorMessage = t('errors.invalid_input'),
      secret,
      min,
      max,
      step,
      defaultValue = undefined,
      isInfoDisplay,
      maxLength,
      autoComplete,
    },
    ref,
  ) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const [passwordVisible, setPasswordVisible] = useState(false);

    const inputType = secret ? (passwordVisible ? 'text' : 'password') : type;

    useEffect(() => {
      if (typeof ref === 'function') {
        ref(inputRef.current);
      } else if (ref && 'current' in ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = inputRef.current;
      }
    }, [ref]);

    const handleClearInput = () => {
      if (onChange) {
        onChange({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
      }
    };

    const handleVariableInsertionClick = () => {
      setIsModalOpen(true);
    };

    const handleEyeClick = () => {
      setPasswordVisible(prev => !prev);
    };

    const handleVariableSelect = (variableName: string) => {
      if (!value) return;
      setIsModalOpen(false);
      if (inputRef.current && onChange) {
        const cursorPosition = inputRef.current.selectionStart || 0;
        const newValue = `${(value as string).slice(
          0,
          cursorPosition,
        )}<span class="variableInput">${variableName}</span>${(value as string).slice(cursorPosition)}`;
        onChange({ target: { value: newValue } } as ChangeEvent<HTMLInputElement>);
      }
    };

    return (
      <div
        className={classNames(styles.root, className, { [styles.hasIcon]: icon, [styles.infoDisplay]: isInfoDisplay })}>
        {label && <p className="typo-text-14--bold text-faded mb-12">{label}</p>}
        <div className="relative">
          {icon && <Icon name={icon} width={20} height={20} className={styles.icon} />}
          <input
            className="overflow-hidden text-ellipsis"
            id={id}
            type={inputType}
            value={value ?? ''}
            defaultValue={defaultValue || undefined}
            onChange={e => {
              const val = e.target.value;
              if (maxLength && val.length > maxLength) return;
              if (onChange) onChange(e);
            }}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            ref={inputRef}
            name={name}
            disabled={disabled}
            min={min}
            max={max}
            step={step}
            maxLength={maxLength}
            autoComplete={autoComplete}
          />

          {!!value && clearable && (
            <div onClick={handleClearInput} onKeyDown={handleClearInput} role="presentation">
              <Icon name="close" width={20} height={20} className={styles.clearableIcon} />
            </div>
          )}
          {secret && (
            <div
              onClick={handleEyeClick}
              role="presentation"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer">
              <Icon
                name={passwordVisible ? 'eye' : 'visibilityOff'}
                width={20}
                height={20}
                className={styles.eyeIcon}
              />
            </div>
          )}
          {hasVariableInsertion && (
            <div onClick={handleVariableInsertionClick} role="presentation">
              <Icon
                name="variable"
                width={20}
                height={20}
                className={`variableIcon absolute right-2 top-1/2 transform -translate-y-1/2 text-faded cursor-pointer opacity-60 ${isModalOpen ? 'open' : ''}`}
              />
            </div>
          )}
        </div>
        <div className="absolute left-[30rem] bottom-[2.5rem]">
          <SelectVariableModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            listItems={variables || []}
            onItemSelect={handleVariableSelect}
          />
        </div>
        {error && (
          <div className="flex align-center gap-x-4 mt-8 status-error-full">
            <Icon name="error" width={16} height={16} />
            <p className="typo-text-12--bold">{errorMessage}</p>
          </div>
        )}
      </div>
    );
  },
);

interface ControlledInputProps extends Omit<InputProps, 'value' | 'onChange'> {
  control: Control<any>; // Use generics for better type safety if desired
  name: string;
  rules?: any;
  formatDisplay?: (value: any) => string;
  numericLocale?: string | null;
}

const getDecimalSeparator = (locale: string): string => {
  const bcp47 = locale.includes('_') ? locale.replace('_', '-') : locale;
  return new Intl.NumberFormat(bcp47).formatToParts(1.1).find(p => p.type === 'decimal')?.value ?? '.';
};

// Annotated as ForwardRefExoticComponent rather than React.FC: it *is* a
// forwardRef down to the DOM input, and the old React.FC annotation hid that
// from TypeScript, so callers could not pass a ref at all.
export const ControlledInput: React.ForwardRefExoticComponent<
  ControlledInputProps & React.RefAttributes<HTMLInputElement>
> = React.forwardRef<HTMLInputElement, ControlledInputProps>(
  ({ control, name, rules, error, errorMessage, formatDisplay, numericLocale, ...rest }, ref) => {
    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState }) => {
          const shouldFormat = rest.disabled && formatDisplay && field.value != null && field.value !== '';
          const isLocaleNumeric = !rest.disabled && numericLocale != null;

          let displayValue = field.value;
          let inputType = rest.type;
          let { onChange } = field;

          if (shouldFormat) {
            displayValue = formatDisplay(field.value);
            inputType = 'text';
          } else if (isLocaleNumeric) {
            inputType = 'text';
            const decimalSep = getDecimalSeparator(numericLocale);
            if (field.value != null && field.value !== '') {
              displayValue = String(field.value).replace('.', decimalSep);
            }
            onChange = (e: ChangeEvent<HTMLInputElement>) => {
              const input = e.target.value;
              const normalized = input.replace(',', '.');
              if (normalized === '' || /^-?\d*\.?\d*$/.test(normalized)) {
                field.onChange(normalized);
              }
            };
          }

          return (
            <Input
              {...field}
              ref={ref}
              error={error ?? !!fieldState.error}
              errorMessage={errorMessage ?? fieldState.error?.message}
              {...rest}
              type={inputType}
              onChange={onChange}
              value={displayValue}
            />
          );
        }}
      />
    );
  },
);

export default Input;
